// src/pages/SupplierMonthlyLessonsReport.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { useUser } from '../contexts/UserContext';
import { useLessons } from '../contexts/LessonsContext';
import SupplierMonthlyReportHeader from '../components/ui/SupplierMonthlyReportHeader';
import MonthPicker from '../components/ui/Monthpicker';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';

/* ===================== Helpers ===================== */
const pad2 = (n) => String(n).padStart(2, '0');

const money = (n) =>
  Number(n || 0).toLocaleString('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 'תאריך לא תקין';
  return d.toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'short' });
};

/* ===================== Small UI atoms ===================== */
function MoneyCell({ value, color, bold = false }) {
  const styles = {
    blue:    'bg-blue-50 text-blue-700 border-blue-100',
    green:   'bg-green-50 text-green-700 border-green-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    purple:  'bg-purple-50 text-purple-700 border-purple-100',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs border whitespace-nowrap ${bold ? 'font-bold' : 'font-semibold'} ${styles[color]}`}>
      ₪{value}
    </span>
  );
}

const getRowStatusClass = () => 'hover:bg-emerald-50/50';
const getStatusAccentBarClass = () => 'bg-gradient-to-r from-emerald-400 to-green-500';

/* ===================== Page ===================== */
const SupplierMonthlyLessonsReport = () => {
  const { user, showAlert } = useUser();
  const { getSupplierTeamDoneLessonsByMonth } = useLessons();

  // Stable date — not recreated on every render
  const now = useRef(new Date()).current;
  const currentMonthStr = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;

  const [month,    setMonth]    = useState(currentMonthStr);
  const [loading,  setLoading]  = useState(false);
  const [lessons,  setLessons]  = useState([]);
  const [viewMode, setViewMode] = useState('table');

  const isSupplier = (user?.role || 'student') === 'supplier';

  useEffect(() => {
    if (!isSupplier) return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const rows = await getSupplierTeamDoneLessonsByMonth(month);
        if (cancelled) return;
        setLessons(Array.isArray(rows) ? rows : []);
      } catch (e) {
        if (cancelled) return;
        showAlert?.(e?.message || 'שגיאה בטעינת הדוח', 'error');
        setLessons([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [month, isSupplier, getSupplierTeamDoneLessonsByMonth, showAlert]);

  const totals = useMemo(() => {
    const totalLessons      = lessons.length;
    const paid               = lessons.reduce((s, l) => s + (Number(l.paidParticipants)     || 0), 0);
    const studentsPaidTotal  = lessons.reduce((s, l) => s + (Number(l.studentsPaidAmount)    || 0), 0);
    const regularPaymentTotal= lessons.reduce((s, l) => s + (Number(l.regularPaymentAmount)  || 0), 0);
    const platformFeeTotal   = lessons.reduce((s, l) => s + (Number(l.platformFeeAmount)     || 0), 0);
    const taxTotal           = lessons.reduce((s, l) => s + (Number(l.taxAmount)             || 0), 0);
    const realIncomeTotal    = lessons.reduce((s, l) => s + (Number(l.supplierRealIncome)    || 0), 0);
    const teacherIds         = new Set(lessons.map((l) => l.teacherId).filter(Boolean));
    const avgIncomePerLesson = totalLessons > 0 ? realIncomeTotal / totalLessons : 0;
    const taxPercent         = lessons.find((l) => l.taxPercent != null)?.taxPercent ?? 0;
    return {
      totalLessons, paid, studentsPaidTotal, regularPaymentTotal, platformFeeTotal, taxTotal,
      realIncomeTotal, activeTeachers: teacherIds.size, avgIncomePerLesson, taxPercent,
    };
  }, [lessons]);

  const exportToExcel = () => {
    try {
      if (!lessons.length) {
        showAlert?.('אין נתונים לייצוא עבור החודש הנבחר', 'error');
        return;
      }
      const rows = lessons.map((l) => ({
        'מזהה שיעור':       l.id ?? '',
        'תאריך':             (l.date || '').toString(),
        'שעת התחלה':         (l.startTime || '').toString().slice(0, 5),
        'שעת סיום':          (l.endTime || '').toString().slice(0, 5),
        'נושא / כותרת':      l.topic || '',
        'קורס':              l.courseTitle || '',
        'מורה':              l.teacherName || '',
        'תלמידים ששילמו':    Number(l.paidParticipants || 0),
        'תשלומי תלמידים (נומינלי)': Number(l.studentsPaidAmount || 0),
        'תשלום רגיל (ברוטו)': Number(l.regularPaymentAmount || 0),
        'עמלת פלטפורמה':      Number(l.platformFeeAmount || 0),
        'נטו לאחר עמלה':      Number(l.netAfterCommission || 0),
        '% ניכוי מס במקור':   Number(l.taxPercent || 0),
        'ניכוי מס במקור':     Number(l.taxAmount || 0),
        'הכנסה בפועל (נטו)':  Number(l.supplierRealIncome || 0),
        'מטבע':              l.currency || 'ILS',
      }));

      rows.push({
        'מזהה שיעור': 'סה"כ', 'תאריך': '', 'שעת התחלה': '', 'שעת סיום': '',
        'נושא / כותרת': '', 'קורס': '', 'מורה': '',
        'תלמידים ששילמו':               totals.paid,
        'תשלומי תלמידים (נומינלי)':     Number(totals.studentsPaidTotal   || 0),
        'תשלום רגיל (ברוטו)':          Number(totals.regularPaymentTotal || 0),
        'עמלת פלטפורמה':                Number(totals.platformFeeTotal    || 0),
        'נטו לאחר עמלה':                Number((totals.regularPaymentTotal || 0) - (totals.platformFeeTotal || 0)),
        '% ניכוי מס במקור':             Number(totals.taxPercent || 0),
        'ניכוי מס במקור':               Number(totals.taxTotal || 0),
        'הכנסה בפועל (נטו)':            Number(totals.realIncomeTotal   || 0),
        'מטבע': lessons[0]?.currency || 'ILS',
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [12, 14, 12, 12, 30, 24, 20, 16, 20, 16, 14, 14, 14, 14, 18, 10].map((wch) => ({ wch }));
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: 15, r: rows.length - 1 } }) };
      wb.Workbook = wb.Workbook || {};
      wb.Workbook.Views = [{ RTL: true }];
      XLSX.utils.book_append_sheet(wb, ws, 'דוח חודשי');
      XLSX.writeFileXLSX(wb, `doroos_supplier_monthly_report_${month}.xlsx`);
      showAlert?.('הדוח יוצא בהצלחה לקובץ Excel', 'success');
    } catch (e) {
      showAlert?.(e?.message || 'שגיאה בייצוא ל-Excel', 'error');
    }
  };

  /* ── Not a supplier ── */
  if (!isSupplier) {
    return (
      <>
        <SupplierMonthlyReportHeader title="דוח שיעורים חודשי" subtitle="עמוד זה זמין למרכזי למידה בלבד" />
        <div className="min-h-screen bg-gray-100 max-w-8xl mx-auto p-4 sm:p-6" dir="rtl">
          <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 11c0 1.657-1.343 3-3 3S6 12.657 6 11s1.343-3 3-3 3 1.343 3 3zm6 10H6a4 4 0 014-4h4a4 4 0 014 4z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">אין הרשאה</h3>
            <p className="text-gray-600">עמוד זה זמין למרכזי למידה בלבד.</p>
          </div>
        </div>
      </>
    );
  }

  /* ── Main render ── */
  return (
    <div className="md:min-h-screen bg-gray-100">
      <SupplierMonthlyReportHeader
        title="דוח שיעורים חודשי — צוות המורים"
        subtitle="כל השיעורים שהתקיימו החודש, לכל מורי הצוות, עם ההכנסה האמיתית שלכם"
      />
      {loading && <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />}

      <div className="mx-auto max-w-8xl px-3 sm:px-4 md:px-6 lg:px-8 pt-4 pb-8" dir="rtl">

        {/* ── Month picker ── */}
        <div className="mb-4 sm:mb-6">
          <MonthPicker
            value={month}
            onChange={(v) => setMonth(v)}
            maxDate={now}
            minYear={2023}
            locale="he"
          />
        </div>

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">

          {/* 1 — שיעורים */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 sm:p-4 rounded-xl border border-green-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-green-700 truncate">שיעורים שהתקיימו</p>
                <p className="text-xl sm:text-2xl font-bold text-green-900">{totals.totalLessons}</p>
              </div>
            </div>
          </div>

          {/* 2 — מורים פעילים */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-3 sm:p-4 rounded-xl border border-indigo-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="8" r="3.2" />
                  <path d="M5.5 20a6.5 6.5 0 0 1 13 0" strokeLinecap="round" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-indigo-700 truncate">מורים פעילים החודש</p>
                <p className="text-xl sm:text-2xl font-bold text-indigo-900">{totals.activeTeachers}</p>
              </div>
            </div>
          </div>

          {/* 3 — תלמידים */}
          <div className="bg-gradient-to-r from-blue-50 to-sky-50 p-3 sm:p-4 rounded-xl border border-blue-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-blue-700 truncate">תלמידים משלמים</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-900">{totals.paid}</p>
              </div>
            </div>
          </div>

          {/* 4 — הכנסה בפועל */}
          <div className="bg-gradient-to-r from-purple-50 to-fuchsia-50 p-3 sm:p-4 rounded-xl border border-purple-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-purple-700 truncate">הכנסה נטו (לאחר עמלה ומס)</p>
                <p className="text-xl sm:text-2xl font-bold text-purple-900">₪{money(totals.realIncomeTotal)}</p>
              </div>
            </div>
          </div>

        </div>

        {/* ── Commission / tax breakdown strip ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-3.5 text-center">
            <div className="text-lg font-bold text-gray-900">₪{money(totals.regularPaymentTotal)}</div>
            <div className="text-xs text-gray-500">תשלום רגיל (ברוטו)</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3.5 text-center">
            <div className="text-lg font-bold text-gray-900">₪{money(totals.platformFeeTotal)}</div>
            <div className="text-xs text-gray-500">עמלת פלטפורמה</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3.5 text-center">
            <div className="text-lg font-bold text-gray-900">
              ₪{money(totals.taxTotal)}
              {totals.taxPercent > 0 && <span className="text-xs text-gray-400 mr-1">({totals.taxPercent}%)</span>}
            </div>
            <div className="text-xs text-gray-500">ניכוי מס במקור</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3.5 text-center">
            <div className="text-lg font-bold text-gray-900">₪{money(totals.realIncomeTotal)}</div>
            <div className="text-xs text-gray-500">נטו סופי לתשלום</div>
          </div>
        </div>

        {/* ── Info banner — the key distinction this report exists for ── */}
        <div className="mb-4 sm:mb-5 rounded-2xl border border-indigo-100 bg-indigo-50/70 px-4 py-3">
          <p className="text-sm font-semibold text-indigo-900">הכנסה בפועל</p>
          <p className="text-xs sm:text-sm text-indigo-700 mt-1">
            מבוסס על תשלומים שהתקבלו בקופה באמצעות אמצעי תשלום רגילים או עסקאות קרדיט בלבד, ללא
            תשלומים שבוצעו באמצעות ארנק תוכנית גפ״ן — לאחר ניכוי עמלת הפלטפורמה וניכוי מס במקור לפי
            האחוז המאושר שלכם.
          </p>
        </div>

        {/* ── View toggle + Export ── */}
        <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
            {['table', 'cards'].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 sm:px-5 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                  viewMode === mode
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {mode === 'table' ? 'טבלה' : 'כרטיסים'}
              </button>
            ))}
          </div>

          <button
            onClick={exportToExcel}
            disabled={loading || lessons.length === 0}
            className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            ייצוא ל-Excel
          </button>
        </div>

        {/* ── Empty state ── */}
        {lessons.length === 0 && !loading ? (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8 7V3m8 4V3M4 11h16M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">אין נתונים לחודש שנבחר</h3>
            <p className="text-gray-600 text-sm">נסה לבחור חודש אחר</p>
          </div>

        ) : viewMode === 'table' ? (
          /*
            Column visibility strategy, same tiering as the teacher report:
            xs/sm  (< 768px)  : תאריך | שעה | נושא | הכנסה בפועל
            md     (≥ 768px)  : + מורה + קורס
            lg     (≥ 1024px) : + תלמידים + תשלומי תלמידים
            xl     (≥ 1280px) : + עמלת פלטפורמה + ניכוי מס
          */
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[440px] md:min-w-[680px] lg:min-w-[900px] xl:min-w-[1120px] border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                    <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">תאריך</th>
                    <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">שעה</th>
                    <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">נושא</th>
                    <th className="hidden md:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">מורה</th>
                    <th className="hidden md:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">קורס</th>
                    <th className="hidden lg:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">תלמידים</th>
                    <th className="hidden lg:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">תשלומי תלמידים</th>
                    <th className="hidden xl:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">עמלת פלטפורמה</th>
                    <th className="hidden xl:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">ניכוי מס</th>
                    <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">הכנסה בפועל</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-50">
                  {lessons.map((lesson, idx) => {
                    const date               = (lesson.date || '').toString();
                    const startTime          = (lesson.startTime || '').toString().slice(0, 5);
                    const endTime            = (lesson.endTime || '').toString().slice(0, 5);
                    const topic              = lesson.topic || '';
                    const course             = lesson.courseTitle || '';
                    const teacherName        = lesson.teacherName || '';
                    const paid               = Number(lesson.paidParticipants || 0);
                    const studentsPaidAmount = Number(lesson.studentsPaidAmount || 0);
                    const platformFeeAmount  = Number(lesson.platformFeeAmount || 0);
                    const taxAmount          = Number(lesson.taxAmount || 0);
                    const taxPercent         = Number(lesson.taxPercent || 0);
                    const realIncome         = Number(lesson.supplierRealIncome || 0);

                    return (
                      <tr
                        key={lesson.id || `${date}-${startTime}-${idx}`}
                        className={`${getRowStatusClass()} transition-colors duration-150`}
                      >
                        <td className="px-3 sm:px-4 py-3 text-xs text-gray-800 whitespace-nowrap font-medium">
                          {formatDate(date)}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                          {startTime}{endTime ? `–${endTime}` : ''}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-xs text-gray-900">
                          <div className="max-w-[140px] sm:max-w-[200px] truncate font-medium" title={topic}>
                            {topic || '—'}
                          </div>
                        </td>
                        <td className="hidden md:table-cell px-3 sm:px-4 py-3 text-xs text-gray-700">
                          <div className="max-w-[140px] truncate font-medium" title={teacherName}>{teacherName || '—'}</div>
                        </td>
                        <td className="hidden md:table-cell px-3 sm:px-4 py-3 text-xs text-gray-500">
                          <div className="max-w-[160px] truncate" title={course}>{course || '—'}</div>
                        </td>
                        <td className="hidden lg:table-cell px-3 sm:px-4 py-3">
                          <span className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold border border-indigo-100">
                            {paid}
                          </span>
                        </td>
                        <td className="hidden lg:table-cell px-3 sm:px-4 py-3 whitespace-nowrap">
                          <MoneyCell value={money(studentsPaidAmount)} color="blue" />
                        </td>
                        <td className="hidden xl:table-cell px-3 sm:px-4 py-3 whitespace-nowrap">
                          <MoneyCell value={money(platformFeeAmount)} color="green" />
                        </td>
                        <td className="hidden xl:table-cell px-3 sm:px-4 py-3 whitespace-nowrap">
                          <span className="text-xs text-gray-500">
                            ₪{money(taxAmount)}{taxPercent > 0 && <span className="text-gray-400"> ({taxPercent}%)</span>}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                          <MoneyCell value={money(realIncome)} color="purple" bold />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        ) : (
          /* ── Cards view ── */
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-4">
            {lessons.map((lesson, idx) => {
              const date               = (lesson.date || '').toString();
              const startTime          = (lesson.startTime || '').toString().slice(0, 5);
              const endTime            = (lesson.endTime || '').toString().slice(0, 5);
              const topic              = lesson.topic || '';
              const course             = lesson.courseTitle || '';
              const teacherName        = lesson.teacherName || '';
              const paid               = Number(lesson.paidParticipants || 0);
              const studentsPaidAmount = Number(lesson.studentsPaidAmount || 0);
              const platformFeeAmount  = Number(lesson.platformFeeAmount || 0);
              const taxAmount          = Number(lesson.taxAmount || 0);
              const taxPercent         = Number(lesson.taxPercent || 0);
              const realIncome         = Number(lesson.supplierRealIncome || 0);

              return (
                <div
                  key={lesson.id || `${date}-${startTime}-${idx}`}
                  className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden"
                >
                  <div className={`h-1 w-full ${getStatusAccentBarClass()}`} />

                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-sm font-semibold text-gray-900 truncate leading-snug" title={topic}>
                        {topic || '—'}
                      </p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border border-emerald-200 bg-emerald-50 text-emerald-700 whitespace-nowrap">
                        בוצע
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-0.5">
                      <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" />
                      </svg>
                      <span>{formatDate(date)}</span>
                      {startTime && (
                        <>
                          <span className="text-gray-300">·</span>
                          <span>{startTime}{endTime ? `–${endTime}` : ''}</span>
                        </>
                      )}
                    </div>

                    {teacherName && (
                      <p className="text-xs text-gray-500 truncate mb-0.5">מורה: {teacherName}</p>
                    )}
                    {course && (
                      <p className="text-xs text-gray-400 truncate mb-1" title={course}>{course}</p>
                    )}

                    <div className="border-t border-gray-100 mt-3 pt-3 grid grid-cols-2 gap-2">
                      <div className="bg-indigo-50 rounded-xl p-2.5">
                        <p className="text-[10px] text-indigo-500 mb-0.5">תלמידים משלמים</p>
                        <p className="text-sm font-bold text-indigo-700">{paid}</p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-2.5">
                        <p className="text-[10px] text-blue-500 mb-0.5">תשלומי תלמידים</p>
                        <p className="text-sm font-bold text-blue-700">₪{money(studentsPaidAmount)}</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-2.5">
                        <p className="text-[10px] text-green-600 mb-0.5">עמלת פלטפורמה</p>
                        <p className="text-sm font-bold text-green-700">₪{money(platformFeeAmount)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-2.5">
                        <p className="text-[10px] text-gray-500 mb-0.5">ניכוי מס {taxPercent > 0 ? `(${taxPercent}%)` : ''}</p>
                        <p className="text-sm font-bold text-gray-700">₪{money(taxAmount)}</p>
                      </div>
                      <div className="col-span-2 bg-gradient-to-l from-purple-50 to-fuchsia-50 rounded-xl p-2.5 border border-purple-100">
                        <p className="text-[10px] text-purple-600 mb-0.5">הכנסה נטו (לאחר עמלה ומס)</p>
                        <p className="text-base font-extrabold text-purple-700">₪{money(realIncome)}</p>
                        <p className="text-[10px] text-purple-500 mt-0.5">תשלומים רגילים או קרדיט בלבד, ללא ארנק תוכנית גפ״ן</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierMonthlyLessonsReport;